import React from "react";
import { AlertTriangle, RefreshCcw, History, ClipboardCopy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportClientError, newCorrelationId } from "@/lib/diagnostics";
import { toast } from "sonner";

// Isolates a single widget: a render crash shows an inline error instead of
// blanking the whole dashboard (Item 192.2/192.3).
export class WidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, cid: null };
  }
  static getDerivedStateFromError(error) {
    return { error, cid: newCorrelationId() };
  }
  componentDidCatch(error, info) {
    reportClientError({
      stage: "widget_render",
      message: error?.message || String(error),
      stack: (error?.stack || "") + "\n" + (info?.componentStack || "").slice(0, 1200),
      dashboard_id: this.props.dashboardId || null,
      widget_id: this.props.widgetId || null,
      widget_type: this.props.widgetType || null,
      correlation_id: this.state.cid,
    });
  }
  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, cid: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div data-testid="widget-error-boundary" className="h-full w-full flex items-center justify-center p-3">
          <div className="text-center space-y-2">
            <AlertTriangle className="w-5 h-5 mx-auto text-[color:var(--danger)]" />
            <div className="text-xs font-medium text-[color:var(--ink-2)]">This widget hit a rendering error</div>
            <div className="text-[10px] text-[color:var(--ink-3)] max-w-[220px] mx-auto truncate" title={this.state.error?.message}>
              {this.state.error?.message || "Unknown error"} · ref {this.state.cid}
            </div>
            <button data-testid="widget-error-retry" onClick={() => this.setState({ error: null, cid: null })}
              className="text-[11px] px-2.5 py-1 rounded-md border border-[color:var(--glass-border)] text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)] inline-flex items-center gap-1">
              <RefreshCcw className="w-3 h-3" />Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Page-level recovery screen (Item 192.15): Retry / Restore last working version / Report.
export class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, cid: null, restoring: false };
  }
  static getDerivedStateFromError(error) {
    return { error, cid: newCorrelationId() };
  }
  componentDidCatch(error, info) {
    reportClientError({
      stage: "dashboard_render",
      message: error?.message || String(error),
      stack: (error?.stack || "") + "\n" + (info?.componentStack || "").slice(0, 1200),
      dashboard_id: this.props.dashboardId || null,
      correlation_id: this.state.cid,
    });
  }
  copyReport = () => {
    const txt = `EIP render failure\nref: ${this.state.cid}\ndashboard: ${this.props.dashboardId || "?"}\nurl: ${window.location.href}\nerror: ${this.state.error?.message}\n${(this.state.error?.stack || "").slice(0, 800)}`;
    navigator.clipboard?.writeText(txt).then(() => toast.success("Details copied — share with your admin"));
  };
  restore = async () => {
    if (!this.props.onRestore) return;
    this.setState({ restoring: true });
    try { await this.props.onRestore(); }
    catch (e) { toast.error(e?.message || "Restore failed"); this.setState({ restoring: false }); }
  };
  render() {
    if (this.state.error) {
      return (
        <div data-testid="dashboard-error-boundary" className="min-h-screen flex items-center justify-center bi-canvas p-6">
          <div className="max-w-md w-full rounded-2xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] p-6 text-center space-y-4">
            <AlertTriangle className="w-8 h-8 mx-auto text-[color:var(--danger)]" />
            <div>
              <div className="font-display text-lg text-[color:var(--ink)]">Something went wrong rendering this dashboard</div>
              <p className="text-xs text-[color:var(--ink-3)] mt-1">
                Your data is safe. Error ref <span className="font-mono">{this.state.cid}</span> — {String(this.state.error?.message || "").slice(0, 140)}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button data-testid="recovery-retry" onClick={() => this.setState({ error: null, cid: null })} className="bg-[color:var(--brand-ocean)] text-white">
                <RefreshCcw className="w-4 h-4 mr-1.5" />Retry
              </Button>
              {this.props.onRestore && (
                <Button data-testid="recovery-restore" variant="outline" onClick={this.restore} disabled={this.state.restoring} className="bg-[color:var(--surface)]">
                  <History className="w-4 h-4 mr-1.5" />{this.state.restoring ? "Restoring…" : "Restore last working version"}
                </Button>
              )}
              <Button data-testid="recovery-report" variant="ghost" onClick={this.copyReport} className="text-[color:var(--ink-3)]">
                <ClipboardCopy className="w-4 h-4 mr-1.5" />Copy error report
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
